import { ref } from 'vue'
import api from '@/services/api'
import { SUP_ST_DOC_ENT } from '@/constants/SUP_ST_DOC_ENT'
import { FIN_TP_TAX_CONVERT } from '@/constants/FIN_TP_TAX_CONVERT'

/**
 * Monta o payload para PUT de DocumentoEntrada mantendo todos os campos
 * inalterados exceto o status.
 */
function montarPayloadDocumento (doc) {
  return {
    idOrganizacao: doc.idOrganizacao ?? null,
    idFilial: doc.idFilial ?? null,
    idParceiroNegocioEmissor: doc.idParceiroNegocioEmissor,
    idTipoDocumentoEntrada: doc.idTipoDocumentoEntrada,
    numeroDocumento: doc.numeroDocumento,
    serieDocumento: doc.serieDocumento,
    dataEmissao: doc.dataEmissao,
    dataVencimento: doc.dataVencimento,
    dataFatoGerador: doc.dataFatoGerador,
    idCondicaoPagamento: doc.idCondicaoPagamento,
    idPurchaseOrder: doc.idPurchaseOrder,
    idOrdemCompra: doc.idOrdemCompra,
    idEmbarque: doc.idEmbarque,
    idOperacao: doc.idOperacao,
    isInterno: doc.isInterno,
    lstMoeda: doc.lstMoeda,
    lstTipoTaxaConversao: doc.lstTipoTaxaConversao ?? null,
    idLocalEstoque: doc.idLocalEstoque ?? null,
    idEmbarqueContainer: doc.idEmbarqueContainer ?? null,
    lstFormaPagamento: doc.lstFormaPagamento ?? null,
    lstFormaPagamentoInternacional: doc.lstFormaPagamentoInternacional ?? null,
    idParceiroEnderecoFaturamento: doc.idParceiroEnderecoFaturamento ?? null,
    idParceiroEnderecoExpedicao: doc.idParceiroEnderecoExpedicao ?? null,
    valorTotalDocumento: doc.valorTotalDocumento,
    valorTotalOriginal: doc.valorTotalOriginal ?? 0,
    valorTotalDesconto: doc.valorTotalDesconto ?? 0,
    isIntegradoFinanceiro: doc.isIntegradoFinanceiro,
    isIntegradoEstoque: doc.isIntegradoEstoque
  }
}

/**
 * useDocumentoEntradaAcoes
 *
 * Composável híbrido que centraliza as ações de aprovação, cancelamento
 * e exclusão de Documentos de Entrada.
 */
export function useDocumentoEntradaAcoes (options = {}) {
  const { onSuccess, showNotification } = options

  const notificar = (payload) => {
    if (showNotification) {
      showNotification(payload)
    }
  }

  /**
   * Extrai a mensagem de erro real da resposta da API.
   * Se a API retornar { message, erros: [...] }, concatena tudo.
   */
  const extrairMensagemErro = (e, fallback) => {
    const data = e?.response?.data
    if (!data) return fallback
    let msg = data.message || ''
    if (Array.isArray(data.erros) && data.erros.length > 0) {
      msg += msg ? '\n' : ''
      msg += data.erros.join('\n')
    }
    return msg || fallback
  }

  // ============================================================
  // ESTADO DO MODAL DE CONFIRMAÇÃO
  // ============================================================
  const modalConfig = ref({
    title: 'Confirmação',
    message: 'Tem certeza?',
    confirmButtonText: 'Confirmar',
    cancelButtonText: 'Cancelar',
    tipoAcao: ''
  })

  const pendingDocumento = ref(null)
  const pendingAcao = ref(null)
  const isLoadingAcao = ref(false)

  // ============================================================
  // CONFIGURAÇÕES DOS MODAIS POR TIPO DE AÇÃO
  // ============================================================
  const configPorAcao = {
    aprovar: {
      title: 'Aprovar Documento',
      message: (doc) => `Deseja realmente aprovar o documento <strong>${doc.numeroDocumento}</strong>?`,
      confirmButtonText: 'Sim, Aprovar',
      cancelButtonText: 'Não, Voltar',
      tipoAcao: 'success'
    },
    cancelar: {
      title: 'Cancelar Documento',
      message: (doc) => `Deseja realmente cancelar o documento <strong>${doc.numeroDocumento}</strong>? Esta ação não poderá ser desfeita.`,
      confirmButtonText: 'Sim, Cancelar',
      cancelButtonText: 'Não, Voltar',
      tipoAcao: 'danger'
    },
    excluir: {
      title: 'Excluir Documento',
      message: (doc) => `Deseja realmente excluir permanentemente o documento <strong>${doc.numeroDocumento}</strong>? Esta ação não poderá ser desfeita.`,
      confirmButtonText: 'Sim, Excluir',
      cancelButtonText: 'Não, Voltar',
      tipoAcao: 'danger'
    },
    cancelarAprovado: {
      title: 'Funcionalidade em Construção',
      message: () => 'Funcionalidade em construção. Deseja modificar o status?',
      confirmButtonText: 'Sim, Modificar',
      cancelButtonText: 'Não, Voltar',
      tipoAcao: 'warning'
    }
  }

  // ============================================================
  // FUNÇÕES DE CONSULTA AOS CADASTROS
  // ============================================================

  // ============================================================
  // ESTADO PARA FLUXO DE APROVAÇÃO COM MODAL
  // ============================================================
  const approvalModalState = ref(null)

  /**
   * Inicia o fluxo de aprovação com verificação dos cadastros
   */
  const iniciarAprovacaoComVerificacao = async (doc) => {
    if (!doc?.idDocumentoEntrada) return

    isLoadingAcao.value = true
    try {
      // O Tipo de Taxa de Conversão vem do componente pai (ex: Ordem de Compra).
      // Se o documento não tiver o valor, aplica o fallback
      // "PTAX Emissão Documento Entrada" (FIN_TP_TAX_CONVERT1).
      const taxaConversao = doc.lstTipoTaxaConversao || FIN_TP_TAX_CONVERT.PTAXDataEmissaoDocumentoEntrada
      const precisaPreencherTaxa = false

      // A modalidade de rateio é um snapshot por item (DocumentoEntradaItem),
      // resolvida a partir do ProdutoFornecedor. Não é mais gravada no cabeçalho.
      if (!precisaPreencherTaxa) {
        const payload = montarPayloadDocumento(doc)
        payload.lstTipoTaxaConversao = taxaConversao
        payload.lstStatusDocumento = SUP_ST_DOC_ENT.Aprovado

        await api.put(`/DocumentosEntrada/${doc.idDocumentoEntrada}`, payload)
        notificar({ message: 'Documento aprovado com sucesso.', type: 'success' })
        if (onSuccess) onSuccess('aprovar', doc)
        return
      }

      // Preparar payload parcial
      const payloadParcial = {
        ...doc,
        lstTipoTaxaConversao: taxaConversao
      }

      // Abrir modal em modo aprovação
      approvalModalState.value = {
        documento: payloadParcial,
        camposPendentes: {
          lstTipoTaxaConversao: precisaPreencherTaxa
        }
      }
    } catch (error) {
      notificar({ message: extrairMensagemErro(error, 'Erro ao verificar campos para aprovação.'), type: 'error' })
    } finally {
      isLoadingAcao.value = false
    }
  }

  /**
   * Executa a aprovação quando o modal é confirmado
   */
  const executarAprovacaoViaModal = async (payloadFinal) => {
    isLoadingAcao.value = true
    try {
      payloadFinal.lstStatusDocumento = SUP_ST_DOC_ENT.Aprovado
      await api.put(`/DocumentosEntrada/${payloadFinal.idDocumentoEntrada}`, payloadFinal)
      notificar({ message: 'Documento aprovado com sucesso.', type: 'success' })
      if (onSuccess) onSuccess('aprovar', payloadFinal)
      approvalModalState.value = null
    } catch (error) {
      notificar({ message: extrairMensagemErro(error, 'Erro ao aprovar documento.'), type: 'error' })
    } finally {
      isLoadingAcao.value = false
    }
  }

  /**
   * Cancela o fluxo de aprovação
   */
  const cancelarAprovacaoViaModal = () => {
    notificar({ message: 'Aprovação do Documento de Entrada cancelada pelo usuário.', type: 'warning' })
    approvalModalState.value = null
  }

  // ============================================================
  // AÇÕES DIRETAS (sem confirmação)
  // ============================================================

  const cancelarDocumento = async (doc) => {
    if (!doc?.idDocumentoEntrada) return
    isLoadingAcao.value = true
    try {
      await api.post(`/DocumentosEntrada/${doc.idDocumentoEntrada}/cancelar`, { observacao: '' })
      notificar({ message: 'Documento cancelado com sucesso.', type: 'success' })
      if (onSuccess) onSuccess('cancelar', doc)
    } catch (e) {
      notificar({ message: extrairMensagemErro(e, 'Erro ao cancelar documento.'), type: 'error' })
    } finally {
      isLoadingAcao.value = false
    }
  }

  const aprovarDocumento = async (doc) => {
    if (!doc?.idDocumentoEntrada) return
    isLoadingAcao.value = true
    try {
      await api.post(`/DocumentosEntrada/${doc.idDocumentoEntrada}/aprovar`, { observacao: '' })
      notificar({ message: 'Documento aprovado com sucesso.', type: 'success' })
      if (onSuccess) onSuccess('aprovar', doc)
    } catch (e) {
      notificar({ message: extrairMensagemErro(e, 'Erro ao aprovar documento.'), type: 'error' })
    } finally {
      isLoadingAcao.value = false
    }
  }

  const excluirDocumento = async (doc) => {
    if (!doc?.idDocumentoEntrada) return
    isLoadingAcao.value = true
    try {
      await api.delete(`/DocumentosEntrada/${doc.idDocumentoEntrada}`)
      notificar({ message: 'Documento excluído com sucesso.', type: 'success' })
      if (onSuccess) onSuccess('excluir', doc)
    } catch (e) {
      const errorMsg = e.response?.data?.message || 'Erro ao excluir documento.'
      notificar({ message: errorMsg, type: 'error' })
    } finally {
      isLoadingAcao.value = false
    }
  }

  // ============================================================
  // FLUXO DE CONFIRMAÇÃO
  // ============================================================

  const confirmarAcao = (tipoAcao, doc, confirmationModalRef) => {
    const config = configPorAcao[tipoAcao]
    if (!config) return

    pendingDocumento.value = doc
    pendingAcao.value = tipoAcao
    modalConfig.value = {
      title: config.title,
      message: config.message(doc),
      confirmButtonText: config.confirmButtonText,
      cancelButtonText: config.cancelButtonText,
      tipoAcao: config.tipoAcao
    }

    if (confirmationModalRef?.show) {
      confirmationModalRef.show()
    }
  }

  const onConfirmarAcao = (confirmed) => {
    if (confirmed && pendingDocumento.value && pendingAcao.value) {
      const acao = pendingAcao.value
      const doc = pendingDocumento.value

      switch (acao) {
        case 'cancelar':
          cancelarDocumento(doc)
          break
        case 'aprovar':
          aprovarDocumento(doc)
          break
        case 'excluir':
          excluirDocumento(doc)
          break
        case 'cancelarAprovado':
          notificar({ message: 'Funcionalidade em construção.', type: 'info' })
          break
      }
    }

    pendingDocumento.value = null
    pendingAcao.value = null
  }

  return {
    // Ações imperativas
    aprovarDocumento,
    cancelarDocumento,
    excluirDocumento,

    // Fluxo de aprovação com verificação
    iniciarAprovacaoComVerificacao,
    executarAprovacaoViaModal,
    cancelarAprovacaoViaModal,
    approvalModalState,

    // Estado do modal de confirmação
    modalConfig,
    pendingDocumento,
    pendingAcao,
    isLoadingAcao,

    // Métodos do fluxo de confirmação
    confirmarAcao,
    onConfirmarAcao
  }
}
